import React, {
  Dispatch,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import getImageUrlWithPreset, {
  ProductImagePresetKeys
} from "@RHCommerceDev/utils/getImageUrlWithPreset";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import memoize from "@RHCommerceDev/utils/memoize";
import { cn } from "@RHCommerceDev/utils/cn";
import { countryFallbackImageMapper } from "@RHCommerceDev/resources/countries-config.json";
import { useLanguage } from "@RHCommerceDev/hooks/useLanguage";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";

export interface RHImageProps extends React.ComponentProps<"img"> {
  preset?: ProductImagePresetKeys;
  analyticsId?: string;
  containerProps?: React.ComponentProps<"picture"> & {
    as?: React.ElementType;
  };
  skeletonProps?: {
    className?: string;
    style?: React.CSSProperties;
  };
  skeletonComponent?: React.FC<{
    className?: string;
    style?: React.CSSProperties;
  }>;
  sources?: React.ComponentProps<"source">[];
  setActualImgHeight?: Dispatch<React.SetStateAction<number>>;
  fallbackImageUrl?: string;
  refreshImageOnSourceChange?: boolean;
}

type State = "loading" | "success" | "error";

const DefaultSkeleton = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div
    className={cn("absolute inset-0 animate-pulse bg-gray-500", className)}
    {...props}
  />
);

export const ImageComponent: React.FC<
  React.ComponentProps<"img"> & { state: State }
> = ({ state, className, ...props }) => (
  <img
    {...props}
    className={cn(
      "h-full w-full",
      state === "loading" ? "opacity-0" : "opacity-1",
      className
    )}
  />
);

type ContainerProps<T extends React.ElementType = "div"> = {
  as?: T;
} & Omit<React.ComponentPropsWithoutRef<T>, "as">;

const Container = <T extends React.ElementType = "div">({
  className,
  as,
  ...props
}: ContainerProps<T>) => {
  const Component = as || "div";

  return (
    <Component
      className={cn("relative block h-full w-full", className)}
      {...props}
    />
  );
};
const RHImage = forwardRef<HTMLImageElement, RHImageProps>(
  (
    {
      className,
      src,
      preset,
      analyticsId,
      containerProps,
      skeletonProps,
      skeletonComponent: SkeletonComponent = DefaultSkeleton,
      loading = "lazy",
      onLoad,
      onError,
      sources,
      setActualImgHeight,
      fallbackImageUrl,
      refreshImageOnSourceChange = false,
      ...props
    },
    ref
  ) => {
    const [state, setState] = useState<State>(
      processEnvServer ? "success" : "loading"
    );
    const env = useEnv();

    useEffect(() => {
      if (env?.FEATURE_SR_4657_IMAGE_FALLBACK && refreshImageOnSourceChange)
        setState(processEnvServer ? "success" : "loading");
    }, [src]);

    const lang = useLanguage();

    const imageUrl = useMemo(() => {
      if (!src) {
        return fallbackImageUrl ?? countryFallbackImageMapper[lang ?? "en"];
      }

      if (state === "error") {
        return fallbackImageUrl ?? countryFallbackImageMapper[lang ?? "en"];
      }

      return preset ? getImageUrlWithPreset(src, preset) : src;
    }, [src, state, preset, fallbackImageUrl, lang]);

    const handleLoad = useCallback(
      e => {
        if (state === "error") {
          return;
        }
        const height = e.currentTarget.clientHeight;
        setActualImgHeight?.(height);
        onLoad?.(e);
        setState("success");
      },
      [onLoad, state]
    );

    const handleError = useCallback(
      e => {
        onError?.(e);
        setState("error");
      },
      [onError]
    );

    return (
      <Container {...containerProps}>
        {sources?.map((props, key) => (
          <source key={key} {...props} />
        ))}
        <ImageComponent
          ref={ref}
          src={imageUrl}
          onLoad={handleLoad}
          onError={handleError}
          loading={loading}
          data-analytics-id={analyticsId}
          state={state}
          {...props}
          className={className}
        />
        {state === "loading" && <SkeletonComponent {...skeletonProps} />}
      </Container>
    );
  }
);

export default memoize(RHImage);
